import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Text, Flex, useToast, Button, Skeleton, Switch } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { PostsVirais } from '@/model';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { useAllClub } from '@/services/queries/club';
import { PostsViraisFilters } from './types';
import {
  useDeletePostsViraisMutation,
  useGetPostsViraisById,
  useGetSocialMediaPostsVirais,
} from '@/services/queries/social-media';
import { PostsViraisCreateModal } from './components/PostsViraisCreateModal';
import { PostsViraisEditModal } from './components/PostsViraisEditModal';
import { PostsViraisFilterBox } from './components/PostsViraisFilterBox';
import { PostsViraisTable } from './components/PostsViraisTable/PostsViraisTable';

export const ApplicationSocialMediaVirais = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);
  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(
    ApplicationComponentId.SOCIAL_MEDIA_VIRAL_POSTS,
    ApplicationComponentId.SOCIAL_MEDIA,
  );
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_social_media.social_media_post_viral.toggled_enable_success')
      : t('pages.application_social_media.social_media_post_viral.toggled_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_social_media.social_media_post_viral.failed_enable_error')
      : t('pages.application_social_media.social_media_post_viral.failed_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.SOCIAL_MEDIA_VIRAL_POSTS, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState<PostsVirais | undefined>(undefined);

  const { data: clubs = [] } = useAllClub(true);
  const deleteMutation = useDeletePostsViraisMutation();
  const getItemByUUID = useGetPostsViraisById();

  const filtersForm = useFiltersForm<PostsViraisFilters>({
    defaultValues: {
      team_uuid: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useGetSocialMediaPostsVirais(params);

  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: PostsVirais) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_social_media.social_media_post_viral.delete_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_social_media.social_media_post_viral.delete_error'),
        });
      },
    });
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_social_media.social_media_post_viral.search_error'),
        });
      },
    });
  };

  const onHandleEditDone = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_social_media.social_media_post_viral.title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_social_media.social_media_post_viral.title')}
                </Text>
                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.application_social_media.social_media_post_viral.enabled')
                    : t('pages.application_social_media.social_media_post_viral.disabled')}
                </Switch>
              </>
            )}
          </Flex>

          <Button
            display='flex'
            alignItems='center'
            gap={2}
            onClick={() => setIsCreateModalOpen(true)}
            width={{ base: '100%', md: 'initial' }}
          >
            <ChakraFeatherIcon name='plus-circle' />
            {t('pages.application_social_media.social_media_post_viral.add_button')}
          </Button>
        </Flex>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <PostsViraisFilterBox clubs={clubs} filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <PostsViraisTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={item => onHandleEdit(item.uuid)}
            onDeleteClick={item => onHandleDelete(item)}
          />
        </Flex>
      </SectionBox>

      {selectedItem && (
        <PostsViraisEditModal
          isOpen={!!isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <PostsViraisCreateModal
        isOpen={!!isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_social_media.social_media_post_viral.delete_title')}
        description={
          <Text>
            {t('pages.application_social_media.social_media_post_viral.delete_description', { team: selectedItem?.team_name })}
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_social_media.social_media_post_viral.title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_social_media.social_media_post_viral.toggle_description')
              : t('pages.application_social_media.social_media_post_viral.toggle_description_disabled')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={getItemByUUID.isLoading || isLoadingEnableOrDisableAppArea || deleteMutation.isLoading} />
    </PageContent>
  );
};
